import sha256 from "sha256";
import { constants } from "@/constants";
import { useTrackingStore } from "@/store/tracking";
import { useSessionStore } from "@/store/session";
import PaymentMonitoring, { FLOW_STEPS } from "@/services/paymentMonitoring";
import PageLoader from "@/services/pageLoader";
import messages from "@/i18n/messages.json";

function getProcessingOrderMessage() {
  const lang = window.globalConfig?.lang || "fr";
  return (
    messages[lang]?.messages?.processingOrder ||
    messages.fr.messages.processingOrder
  );
}

function serializeParamPlusQueryValue(key, value) {
  if (key !== "emailDoubleOptin") {
    return value;
  }

  if (typeof value !== "string" || !value.includes("@")) {
    return value;
  }

  return sha256(value.toLowerCase());
}

export default class Redirect {
  static getParamPlusData() {
    let paramPlusData = localStorage.getItem(constants.PARAM_PLUS);
    try {
      paramPlusData = JSON.parse(paramPlusData);
    } catch (error) {
      console.error(error, paramPlusData);
    }
    return paramPlusData;
  }

  static checkAndGetQueryParams(paramPlusData) {
    const queryParams = useTrackingStore().urlParams;
    if (queryParams?.NCError && queryParams.NCError !== "undefined") {
      console.error(
        `Ogone error landing page, ${JSON.stringify(queryParams)}`,
        paramPlusData === null ? "noOrderId" : paramPlusData.orderId,
        queryParams.NCError,
      );

      PaymentMonitoring.captureMessage("waiting_ogone_ncerror", {
        level: "error",
        tags: {
          step: FLOW_STEPS.REDIRECT,
          payment_type: paramPlusData?.paymentType || "unknown",
          action: paramPlusData?.action || "unknown",
          nc_error: queryParams.NCError,
          reason: "ogone_returned_ncerror_on_redirect",
        },
        context: {
          orderId: paramPlusData?.orderId || null,
          hasRetryUrl: Boolean(paramPlusData?.retryUrl),
          authorizationId: queryParams.authorizationId || null,
          retryQuery: queryParams.retry || null,
          explanation: `Ogone redirected back with NCError=${queryParams.NCError}. This means the 3DS/payment flow failed on Worldline side before returning to the waiting page.`,
        },
        fingerprint: ["waiting-ogone-ncerror", queryParams.NCError],
      });
    }
    return queryParams;
  }

  static buildQueryStringFromParamPlus(paramPlusData) {
    if (!paramPlusData || typeof paramPlusData !== "object") {
      return "";
    }

    const excludeParams = [
      "orderId",
      "paymentType",
      "action",
      "retry",
      "retryUrl",
    ];

    return Object.keys(paramPlusData)
      .filter((key) => {
        return !excludeParams.includes(key);
      })
      .map((key) => {
        const value = serializeParamPlusQueryValue(key, paramPlusData[key]);
        return `${key}=${value}`;
      })
      .join("&");
  }

  static getOrderPageBasePath() {
    const slug = window.globalConfig?.orderPageSlug;
    if (slug) {
      return `/${slug}/`;
    }
    return null;
  }

  static buildOrderPageQueryString(paramPlusData, baseParams = {}) {
    const params = new URLSearchParams(baseParams);
    const extraQuery = this.buildQueryStringFromParamPlus(paramPlusData);

    if (extraQuery) {
      for (const pair of extraQuery.split("&")) {
        const [key, value] = pair.split("=");
        if (key && !params.has(key)) {
          params.set(key, value);
        }
      }
    }

    return params.toString();
  }

  static redirectToError(paramPlusData, monitoringContext = {}) {
    console.error(
      "Redirecting to order page with payment error modal",
      paramPlusData,
    );

    const { reason = "generic", ogoneStatus = null } = monitoringContext;
    const isPaymentFailure =
      reason === "payment_failed" || reason === "payment_timeout";

    const monitoringPayload = {
      tags: {
        step: FLOW_STEPS.REDIRECT,
        payment_type: paramPlusData?.paymentType || "unknown",
        order_status: isPaymentFailure ? "palyance_ko" : "error",
        redirect_reason: reason,
      },
      context: {
        orderId: paramPlusData?.orderId || null,
        externalOrderId: paramPlusData?.orderId || null,
        hasRetryUrl: Boolean(paramPlusData?.retryUrl),
        ogoneStatus,
      },
    };

    if (isPaymentFailure) {
      PaymentMonitoring.trackFunnelEvent(
        "redirect_to_error_page",
        monitoringPayload,
      );
    } else {
      PaymentMonitoring.captureMessage("redirect_to_error_page", {
        level: "error",
        ...monitoringPayload,
        fingerprint: ["redirect-to-error-page", reason],
      });
    }

    this.persistBrandLogo();

    const orderPageBasePath = this.getOrderPageBasePath();
    if (orderPageBasePath) {
      const baseParams = { paymentKo: "1" };
      if (paramPlusData?.orderId) {
        baseParams.retry = paramPlusData.orderId;
      }
      const queryString = this.buildOrderPageQueryString(
        paramPlusData,
        baseParams,
      );
      PageLoader.show();
      window.location.href = `${window.location.origin}${orderPageBasePath}${queryString ? "?" + queryString : ""}`;
      return;
    }

    if (paramPlusData?.retryUrl) {
      PageLoader.show();
      window.location.href = paramPlusData.retryUrl;
      return;
    }

    const errorPage = window.globalConfig?.errorPage || "/error";
    const errorBaseUrl = `${window.location.origin}${errorPage}`;
    const queryString = this.buildQueryStringFromParamPlus(paramPlusData);
    PageLoader.show();
    window.location.href = `${errorBaseUrl}${queryString ? "?" + queryString : ""}`;
  }

  static redirectToConfirmation(paramPlusData) {
    console.log("Redirecting to confirmation page", paramPlusData);
    const confirmationPage = window.globalConfig.confirmationPage
      ? window.globalConfig.confirmationPage
      : "/confirmation";
    const confirmationBaseUrl = `${window.location.origin}${confirmationPage}`;
    const queryString = this.buildQueryStringFromParamPlus(paramPlusData);
    PageLoader.show(getProcessingOrderMessage());
    window.location.href = `${confirmationBaseUrl}${queryString ? "?" + queryString : ""}`;
  }

  static redirectToCheckout(paramPlusData) {
    const confirmationBaseUrl = `${window.location.origin}/checkout`;
    const queryString = this.buildQueryStringFromParamPlus(paramPlusData);
    PageLoader.show(getProcessingOrderMessage());
    window.location.href = `${confirmationBaseUrl}${queryString ? "?" + queryString : ""}`;
  }

  static persistBrandLogo() {
    const logo = window.globalConfig?.logo;
    if (!logo) {
      return;
    }

    try {
      window.sessionStorage?.setItem("vanguard.brandLogo", logo);
    } catch (e) {
      // sessionStorage indisponible (mode privé Safari, iframe sandbox, etc.)
    }
  }

  static redirectToAlreadySubscribed(monitoringContext = {}) {
    console.warn(
      "Redirecting to order page with already-subscribed modal",
      monitoringContext,
    );

    const { reason = "duplicate" } = monitoringContext;

    PaymentMonitoring.trackFunnelEvent("redirect_to_already_subscribed_page", {
      tags: {
        step: FLOW_STEPS.REDIRECT,
        order_status: "duplicate",
        redirect_reason: reason,
      },
      context: {
        orderPageSlug: window.globalConfig?.orderPageSlug || null,
      },
    });

    this.persistBrandLogo();

    const orderPageBasePath = this.getOrderPageBasePath();
    if (orderPageBasePath) {
      PageLoader.show();
      window.location.href = `${window.location.origin}${orderPageBasePath}?alreadySubscribed=1`;
      return;
    }

    const alreadySubscribedPage =
      window.globalConfig?.alreadySubscribedPage || "/already-subscribed";
    const pageBaseUrl = `${window.location.origin}${alreadySubscribedPage}`;
    PageLoader.show();
    window.location.href = pageBaseUrl;
  }
}
